/**
 * Validates a phone number according to the following rules:
 * - Only digits 0-9 allowed
 * - Min length: 8, Max length: 15
 * - No repeated digits (8+ consecutive same digit)
 * - No sequential numbers (6+ consecutive sequential digits)
 */
export function validatePhone(phone: string): { isValid: boolean; error?: string } {
  const cleaned = phone.trim()

  if (!cleaned) {
    return { isValid: false, error: 'Phone number is required' }
  }

  if (!/^\d+$/.test(cleaned)) {
    return { isValid: false, error: 'Phone number must contain only digits 0-9' }
  }

  const length = cleaned.length
  if (length < 8) {
    return { isValid: false, error: 'Phone number must be at least 8 digits' }
  }

  if (length > 15) {
    return { isValid: false, error: 'Phone number must be at most 15 digits' }
  }

  if (/(\d)\1{7,}/.test(cleaned)) {
    return { isValid: false, error: 'Phone number cannot contain repeated digits' }
  }

  // Check for sequential patterns (6+ consecutive sequential digits)
  // Early exit optimization: only check if length >= 6
  if (length >= 6) {
    for (let i = 0; i <= length - 6; i++) {
      const first = parseInt(cleaned[i])
      const second = parseInt(cleaned[i + 1])
      const diff = second - first

      // Only check if first two digits are sequential (diff is ±1)
      if (Math.abs(diff) === 1) {
        let isSequential = true
        for (let j = i + 2; j < i + 6; j++) {
          const current = parseInt(cleaned[j])
          const expected = parseInt(cleaned[j - 1]) + diff
          if (current !== expected) {
            isSequential = false
            break
          }
        }
        if (isSequential) {
          return { isValid: false, error: 'Phone number cannot contain sequential numbers' }
        }
      }
    }
  }

  return { isValid: true }
}
